import { MAX_WORD_TOKENS } from '../pack/constants';

/**
 * Word-like segmentation with a hard cap.
 *
 * `Intl.Segmenter` is the only segmenter permitted: it is Unicode-aware, it is in
 * the platform, and the design rules out a third-party NLP library or CJK
 * word-breaking dependency. Using it also makes `what's SHA` two tokens rather
 * than three, which is what lets the contracted wh-grammar run before the short
 * fallback.
 *
 * Segmentation stops as soon as the 513th word-like token is observed. The cap is
 * what makes `S-INPUT-LIMIT` cheap: a pathological paste never segments in full,
 * and every downstream token count is bounded.
 */
export type WordTokenSegmentation = {
	tokens: readonly string[];
	/** True when a 513th word-like token was observed. */
	exceededCap: boolean;
};

/**
 * Fallback used only when `Intl.Segmenter` is missing.
 *
 * Deliberately conservative and Latin-shaped: it keeps intra-word apostrophes so
 * contracted forms stay single tokens, and it treats every other separator as a
 * boundary. A runtime without `Intl.Segmenter` is a degraded environment, not a
 * supported target, so this exists to keep classification running rather than to
 * reproduce UAX #29.
 */
const FALLBACK_WORD = /[\p{L}\p{N}]+(?:['’][\p{L}\p{N}]+)*/gu;

const segmentWithFallback = (qMatch: string): WordTokenSegmentation => {
	const tokens: string[] = [];

	for (const match of qMatch.matchAll(FALLBACK_WORD)) {
		tokens.push(match[0]);

		if (tokens.length > MAX_WORD_TOKENS) {
			return { tokens, exceededCap: true };
		}
	}

	return { tokens, exceededCap: false };
};

/**
 * One segmenter for the module, built on first use.
 *
 * Constructing an `Intl.Segmenter` loads ICU break data, which is far more expensive than
 * running it. Classification happens on every input change with no debounce, so building
 * one per keystroke would dominate the input-handler budget. A segmenter carries no state
 * between `segment()` calls, so reuse is safe.
 */
let sharedSegmenter: Intl.Segmenter | undefined;

const getSegmenter = (): Intl.Segmenter => {
	if (!sharedSegmenter) {
		sharedSegmenter = new Intl.Segmenter(undefined, { granularity: 'word' });
	}

	return sharedSegmenter;
};

export const segmentWordTokens = (qMatch: string): WordTokenSegmentation => {
	if (typeof Intl?.Segmenter !== 'function') {
		return segmentWithFallback(qMatch);
	}

	const segmenter = getSegmenter();
	const tokens: string[] = [];

	for (const segment of segmenter.segment(qMatch)) {
		if (!segment.isWordLike) {
			continue;
		}

		tokens.push(segment.segment);

		// Observing the 513th token is enough to commit Search, so there is no
		// reason to keep segmenting a query that has already failed the bound.
		if (tokens.length > MAX_WORD_TOKENS) {
			return { tokens, exceededCap: true };
		}
	}

	return { tokens, exceededCap: false };
};

const LETTER = /\p{L}/u;

/**
 * Counts Unicode letters in a token, stopping once `max` is exceeded.
 *
 * `S-EMPTY` only needs to tell "exactly one letter" from "more than one", so there is no
 * reason to walk a long token to the end.
 */
export const countLetters = (token: string, max: number = Number.MAX_SAFE_INTEGER): number => {
	let letters = 0;

	for (const codePoint of token) {
		if (LETTER.test(codePoint)) {
			letters += 1;

			if (letters > max) {
				return letters;
			}
		}
	}

	return letters;
};
